/**
 * Focal Point Detection — identifies entities receiving multi-source
 * intelligence attention within a 6-hour window.
 *
 * Algorithm:
 * 1. Scan all events from last 6 hours
 * 2. Extract entity mentions from titles (country names, organizations)
 * 3. Count mentions per entity across distinct sources
 * 4. For entities with 3+ mentions from 2+ sources, compute composite score
 * 5. Classify urgency: score >= 70 critical, >= 50 elevated, else watch
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { FocalPoint, Signal } from './types';
import { getEventTimestamp } from './types';
import { getAllCountryNames, getCountryCodeFromName } from '@/config/country-centroids';

// Source authority tiers for scoring weight
const SOURCE_TIER: Record<string, number> = {
  usgs: 1, gdacs: 1, acled: 1, sanctions: 1,
  firms: 2, gdelt: 2, opensky: 2, vesselfinder: 2,
};

// Structured data sources (for correlation bonus)
const STRUCTURED_SOURCES = new Set([
  'usgs', 'gdacs', 'acled', 'firms', 'gdelt',
  'opensky', 'vesselfinder', 'sanctions',
]);

// Major international organizations
const ORGANIZATIONS: { pattern: string; canonical: string }[] = [
  { pattern: 'nato', canonical: 'NATO' },
  { pattern: 'european union', canonical: 'European Union' },
  { pattern: 'united nations', canonical: 'United Nations' },
  { pattern: 'iaea', canonical: 'IAEA' },
  { pattern: 'world health organization', canonical: 'WHO' },
  { pattern: 'who', canonical: 'WHO' },
  { pattern: 'international monetary fund', canonical: 'IMF' },
  { pattern: 'imf', canonical: 'IMF' },
  { pattern: 'world bank', canonical: 'World Bank' },
  { pattern: 'opec', canonical: 'OPEC' },
  { pattern: 'brics', canonical: 'BRICS' },
  { pattern: 'asean', canonical: 'ASEAN' },
  { pattern: 'african union', canonical: 'African Union' },
  { pattern: 'arab league', canonical: 'Arab League' },
  { pattern: 'wagner group', canonical: 'Wagner Group' },
  { pattern: 'hezbollah', canonical: 'Hezbollah' },
  { pattern: 'hamas', canonical: 'Hamas' },
  { pattern: 'houthi', canonical: 'Houthi' },
  { pattern: 'isis', canonical: 'ISIS' },
  { pattern: 'islamic state', canonical: 'ISIS' },
  { pattern: 'taliban', canonical: 'Taliban' },
  { pattern: 'al-qaeda', canonical: 'Al-Qaeda' },
  { pattern: 'al qaeda', canonical: 'Al-Qaeda' },
  { pattern: 'aukus', canonical: 'AUKUS' },
  { pattern: 'shanghai cooperation', canonical: 'SCO' },
  { pattern: 'g7', canonical: 'G7' },
  { pattern: 'g20', canonical: 'G20' },
];

interface EntityPattern {
  regex: RegExp;
  canonical: string;
  type: 'country' | 'organization';
  code?: string;
}

// Lazy-initialized pattern cache
let patterns: EntityPattern[] | null = null;

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function titleCase(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildEntityPatterns(): EntityPattern[] {
  if (patterns) return patterns;

  const result: EntityPattern[] = [];

  // Country patterns from centroids name map
  const names = getAllCountryNames();
  // Dedupe: track which canonical display name we've already added
  // (many aliases map to the same code, e.g. "usa"/"us"/"america" all → US)
  const seenCountries = new Map<string, boolean>(); // code → added

  for (const name of names) {
    const code = getCountryCodeFromName(name);
    if (!code) continue;

    // Skip very short names (2 chars like "us") to avoid false positives in titles
    // We'll handle these via the location.country field instead
    if (name.length < 3) continue;

    const display = titleCase(name);

    try {
      const escaped = escapeRegex(name);
      result.push({
        regex: new RegExp(`\\b${escaped}\\b`, 'i'),
        canonical: display,
        type: 'country',
        code,
      });
      seenCountries.set(code, true);
    } catch {
      // Skip patterns that produce invalid regex
    }
  }

  // Organization patterns
  for (const org of ORGANIZATIONS) {
    try {
      const escaped = escapeRegex(org.pattern);
      result.push({
        regex: new RegExp(`\\b${escaped}\\b`, 'i'),
        canonical: org.canonical,
        type: 'organization',
      });
    } catch {
      // Skip
    }
  }

  // Sort longest canonical name first — prefer longer matches
  result.sort((a, b) => b.canonical.length - a.canonical.length);

  patterns = result;
  return result;
}

interface EntityMention {
  entity: string;
  type: 'country' | 'organization';
  code?: string;
  source: string;
  event: NormalizedEvent;
}

/**
 * Extract entity mentions from an event's title and description.
 * Also considers the event's location country field.
 */
function extractEntities(
  event: NormalizedEvent,
): { entity: string; type: 'country' | 'organization'; code?: string }[] {
  const text = `${event.title} ${event.description ?? ''}`;
  const entityPatterns = buildEntityPatterns();
  const seen = new Set<string>();
  const results: { entity: string; type: 'country' | 'organization'; code?: string }[] = [];

  for (const p of entityPatterns) {
    if (seen.has(p.canonical)) continue;
    if (p.regex.test(text)) {
      seen.add(p.canonical);
      results.push({ entity: p.canonical, type: p.type, code: p.code });
    }
  }

  // Also check event's location country field
  if (event.location?.country) {
    const code = getCountryCodeFromName(event.location.country);
    if (code) {
      const display = titleCase(event.location.country);
      if (!seen.has(display)) {
        seen.add(display);
        results.push({ entity: display, type: 'country', code });
      }
    }
  }

  if (event.location?.countryCode) {
    // Try to get a display name from a known pattern
    const cc = event.location.countryCode.toUpperCase();
    const existing = results.find(
      (r) => r.type === 'country' && r.code === cc,
    );
    if (!existing) {
      // Use the code as a fallback entity name
      const countryName = event.location.country ?? cc;
      const display = titleCase(countryName);
      if (!seen.has(display)) {
        seen.add(display);
        results.push({ entity: display, type: 'country', code: cc });
      }
    }
  }

  return results;
}

function getSourceTier(source: string): number {
  return SOURCE_TIER[source] ?? 4;
}

/**
 * Detect focal points — entities receiving multi-source intelligence attention.
 * Scans events from the last 6 hours.
 */
export function detectFocalPoints(
  events: NormalizedEvent[],
  timeRange?: '1h' | '6h' | '24h' | '48h' | '7d' | 'all',
): FocalPoint[] {
  const windowMs = timeRange === '1h' ? 1 * 60 * 60 * 1000
    : timeRange === '6h' ? 6 * 60 * 60 * 1000
    : timeRange === '24h' ? 24 * 60 * 60 * 1000
    : timeRange === '48h' ? 48 * 60 * 60 * 1000
    : timeRange === '7d' ? 7 * 24 * 60 * 60 * 1000
    : timeRange === 'all' ? Infinity
    : 24 * 60 * 60 * 1000; // default: 24 hours

  const cutoff = windowMs === Infinity ? 0 : Date.now() - windowMs;
  const recent = events.filter((e) => {
    if (getEventTimestamp(e) < cutoff) return false;
    // Hide untranslated foreign-language content (same filter as news feed)
    if (e.language && e.language !== 'en' && e.language !== 'unknown' && !e.tags?.includes('translated')) return false;
    return true;
  });

  // Don't produce misleading focal points from sparse data
  if (recent.length < 10) return [];

  // Collect all entity mentions
  const mentionsByEntity = new Map<string, EntityMention[]>();

  for (const event of recent) {
    const entities = extractEntities(event);
    for (const { entity, type, code } of entities) {
      if (!mentionsByEntity.has(entity)) {
        mentionsByEntity.set(entity, []);
      }
      mentionsByEntity.get(entity)!.push({
        entity,
        type,
        code,
        source: event.source,
        event,
      });
    }
  }

  const focalPoints: FocalPoint[] = [];
  const now = new Date().toISOString();

  for (const [entity, mentions] of mentionsByEntity) {
    const distinctSources = new Set(mentions.map((m) => m.source));

    // Filter: 3+ mentions from 2+ distinct sources
    if (mentions.length < 5 || distinctSources.size < 2) continue;

    // --- News score (max 30): mentions weighted by source authority ---
    let newsScore = 0;
    for (const m of mentions) {
      const tier = getSourceTier(m.source);
      const weight = tier === 1 ? 1.5 : tier === 2 ? 1.0 : 0.8;
      newsScore += 3 * weight;
    }
    newsScore = Math.min(30, newsScore);

    // --- Signal score: distinct event types, diminishing returns (max 25) ---
    const eventTypes = new Set(mentions.map((m) => m.event.type));
    // 1 type = 8, 2 = 14, 3 = 19, 4 = 22, 5+ = 25
    const signalScore = Math.min(25, Math.round(25 * (1 - Math.exp(-0.4 * eventTypes.size))));

    // --- Correlation bonus (max 15): news + structured data co-occurrence ---
    const hasStructured = mentions.some((m) => STRUCTURED_SOURCES.has(m.source));
    const hasNews = mentions.some((m) => !STRUCTURED_SOURCES.has(m.source));
    const correlationBonus = hasStructured && hasNews ? 15 : hasStructured ? 8 : 0;

    // --- Urgency multiplier: severity-weighted boost (capped at 1.5x) ---
    let urgencyMultiplier = 1.0;
    for (const m of mentions) {
      const sev = m.event.severity ?? 'info';
      if (sev === 'critical') urgencyMultiplier += 0.05;
      else if (sev === 'high') urgencyMultiplier += 0.02;
    }
    urgencyMultiplier = Math.min(1.5, urgencyMultiplier);

    const rawScore =
      (newsScore + signalScore + correlationBonus) * urgencyMultiplier;
    const score = Math.min(100, Math.round(rawScore));

    // Classify urgency level
    const urgencyLevel: FocalPoint['urgencyLevel'] =
      score >= 70 ? 'critical' : score >= 50 ? 'elevated' : 'watch';

    // Build signals array (deduplicated by type+source)
    const signals: Signal[] = [];
    const seenSignals = new Set<string>();
    for (const m of mentions) {
      const key = `${m.event.type}-${m.source}`;
      if (seenSignals.has(key)) continue;
      seenSignals.add(key);
      signals.push({
        type: m.event.type,
        description: m.event.title,
        severity: m.event.severity ?? 'info',
        timestamp: m.event.timestamp,
      });
    }

    // Build structured narrative
    const sourceNames = Array.from(distinctSources).join(', ');
    const typeBreakdown = [...eventTypes].map(t => {
      const count = mentions.filter(m => m.event.type === t).length;
      return `${count} ${t.replace(/_/g, ' ')}`;
    }).join(', ');

    // Get the most severe/recent headline as the lead signal
    const topMention = mentions
      .sort((a, b) => {
        const sevOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
        const sa = sevOrder[a.event.severity ?? 'info'] ?? 4;
        const sb = sevOrder[b.event.severity ?? 'info'] ?? 4;
        return sa - sb || getEventTimestamp(b.event) - getEventTimestamp(a.event);
      })[0];

    const leadSignal = topMention?.event.title ?? '';
    const windowLabel = timeRange === '1h' ? 'hour' : timeRange === '6h' ? '6 hours' : timeRange === '24h' ? '24 hours' : timeRange === '48h' ? '48 hours' : timeRange === '7d' ? '7 days' : '24 hours';
    const narrative = `${leadSignal ? leadSignal + '. ' : ''}${typeBreakdown} across ${sourceNames} in the last ${windowLabel}.`;

    focalPoints.push({
      id: `fp-${entity.toLowerCase().replace(/\s+/g, '-')}`,
      entity,
      urgencyLevel,
      score,
      newsScore: Math.round(newsScore),
      signalScore,
      correlationBonus,
      mentionCount: mentions.length,
      signals: signals.slice(0, 20),
      narrative,
      detectedAt: now,
    });
  }

  // Sort by score descending
  focalPoints.sort((a, b) => b.score - a.score);

  return focalPoints;
}
